// completion-persist.js — the one way a screen records a terminal completion.
//
// Brief 199. Every exercise screen used to write its completion like this:
//
//   this._completionPersisted = true;                       // ← before the write
//   services.learner.markItemCompleted(id)
//     .catch((err) => console.warn("… markItemCompleted failed", err));
//
// Nothing awaited the write, a rejection was logged and dropped, and the latch
// was already set — so that view would never try again, and the learner was
// told nothing. The 2022 full-pathway walk lost six completions that way
// (docs/193-handover.md §2b): no `itemProgress` row at all for items whose
// drills had demonstrably run to their completion screen, each one leaving the
// rest of its lesson behind a shut gate.
//
// The durability itself lives in `learner-store.markItemCompleted` (retry,
// atomic increment, shared in-flight promise) so no call site can forget it.
// What is left for the screens is the part only they can do: await the write,
// set their "already persisted" latch ONLY on success, and — Chris's ruling of
// 2 Nov 2026 — tell the learner when it could not be saved at all. This module
// is that, once, instead of eight times.
//
// Mirrors iOS honouring the write's own result: `KFEDatabaseManager.m:503`
// bumps the count inside `if ([database executeUpdateWithFormat:…])`. iOS has
// no analogue for the learner-facing message because FMDB writes synchronously
// and the failure cannot be deferred past the screen — the message is a
// web-platform addition (Brief 199 criterion 4), not iOS canon.

import { services } from "../platform-services.js";

/**
 * Record a terminal completion and report whether it landed.
 *
 * Awaits the durable write. On a write that failed every retry it announces
 * `kfe:completion-write-failed` on `document` — the app shell renders the ruled
 * message (`completion.writeFailed`, EN + ZH) — and logs once with the item id.
 *
 * @param {number} itemId
 * @param {{ secondsSpent?: number, score?: number, modality?: string, testId?: number,
 *           startedAt?: number }} [detail] exercise-specific CRM enrichment
 * @returns {Promise<boolean>} true when the completion is recorded; false when
 *   it is not, in which case the caller must leave its latch CLEAR so a later
 *   terminal event can try again.
 */
export async function persistCompletion(itemId, detail = undefined) {
  try {
    await services.learner.markItemCompleted(itemId, detail);
    return true;
  } catch (err) {
    console.error("[completion] completion write failed for item", itemId, err);
    if (typeof document !== "undefined") {
      document.dispatchEvent(new CustomEvent("kfe:completion-write-failed", {
        detail: { itemId },
      }));
    }
    return false;
  }
}
